import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Pencil } from "lucide-react";

import { HoldToConfirm } from "@/components/ui/HoldToConfirm";
import { cn } from "@/lib/cn";
import { formatRelativeAge } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { severityBadgeClass } from "@/lib/severity";
import type { AlarmRule, DatapointInfo } from "@/data/alarmRules/wireFormat";
import type { AlarmTargetableDevice } from "@/data/adapter";

import { AckStatusBadge, type AckState } from "./AckStatusBadge";

/** Render-time translate function shape (useT()'s return / tr). */
type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * Operator-readable one-liner per wire rule type (ALM-11), as DICT KEYS —
 * translated at render time (never at module init). Rendered as the title
 * tooltip on the rule-type chips below; exported so the alarms console /
 * compact list can reuse the same wording instead of leaking bare tokens
 * like "stuck" or "delta".
 */
export const RULE_TYPE_LABEL_KEYS: Record<AlarmRule["type"], string> = {
  threshold: "manage.ruleType.threshold",
  range: "manage.ruleType.range",
  boolean: "manage.ruleType.boolean",
  inactivity: "manage.ruleType.inactivity",
  stuck: "manage.ruleType.stuck",
  rate_of_change: "manage.ruleType.rate_of_change",
  delta: "manage.ruleType.delta",
  daily_energy: "manage.ruleType.daily_energy",
  excessive_cycling: "manage.ruleType.excessive_cycling",
  peak_demand: "manage.ruleType.peak_demand",
  idle_consumption: "manage.ruleType.idle_consumption",
  expression: "manage.ruleType.expression",
  composite: "manage.ruleType.composite",
  comm_degradation: "manage.ruleType.comm_degradation",
};

/** Severity names are wire values — translate KNOWN ones for chrome only. */
const KNOWN_SEVERITIES = new Set(["critical", "major", "minor", "warning"]);
function sevLabel(t: Translate, severity: string): string {
  const k = severity.toLowerCase();
  return KNOWN_SEVERITIES.has(k) ? t(`manage.sev.${k}`) : severity;
}

/** "1500" → "25 min", "7200" → "2 h", everything else stays in seconds. */
function fmtDur(s: number): string {
  if (s >= 3600 && s % 3600 === 0) return `${s / 3600} h`;
  if (s >= 60 && s % 60 === 0) return `${s / 60} min`;
  return `${s} s`;
}

/**
 * One-line config summary derived from `rule.config` + type (ALM-08) so
 * the operator can read what a rule checks without opening the editor.
 * Wording mirrors the RuleTypeFields labels (kWh / W / Hz / "units per s")
 * so the list and the form never disagree about units. The static wording
 * is translated through `t`; numbers, units, operators, datapoints and
 * timeframe tokens stay exactly as built (wire values).
 */
export function summarizeRule(rule: AlarmRule, t: Translate): string {
  // datapoint is optional on some rule shapes (composite/expression) —
  // an em-dash placeholder keeps the t() params total and the summary honest.
  const dp = rule.datapoint ?? "—";
  switch (rule.type) {
    case "threshold": {
      const u = rule.config.unit ? ` ${rule.config.unit}` : "";
      return t("manage.ruleSum.threshold", { dp, op: rule.config.operator, value: `${rule.config.threshold}${u}`, dur: fmtDur(rule.config.duration_s) });
    }
    case "range": {
      const c = rule.config;
      return t(c.inside_is_alarm ? "manage.ruleSum.range.inside" : "manage.ruleSum.range.outside", { dp, lo: c.lo, hi: c.hi, dur: fmtDur(c.duration_s) });
    }
    case "boolean":
      return rule.config.duration_s > 0
        ? t("manage.ruleSum.boolean.for", { dp, value: String(rule.config.alarm_when), dur: fmtDur(rule.config.duration_s) })
        : t("manage.ruleSum.boolean", { dp, value: String(rule.config.alarm_when) });
    case "inactivity":
      return t("manage.ruleSum.inactivity", { dp, dur: fmtDur(rule.config.timeout_s) });
    case "stuck": {
      const tol = rule.config.tolerance > 0 ? ` (±${rule.config.tolerance})` : "";
      return t("manage.ruleSum.stuck", { dp, tol, dur: fmtDur(rule.config.window_s) });
    }
    case "rate_of_change": {
      const c = rule.config;
      const dir = c.direction === "both" ? "" : t(`manage.ruleSum.dir.${c.direction}`);
      return t("manage.ruleSum.rateOfChange", { dp, rate: c.max_rate, dur: fmtDur(c.window_s), dir });
    }
    case "delta":
      return t("manage.ruleSum.delta", { dp, delta: rule.config.max_delta, dur: fmtDur(rule.config.window_s) });
    case "daily_energy":
      return t("manage.ruleSum.dailyEnergy", { dp, op: rule.config.operator, value: rule.config.threshold, timeframe: rule.config.timeframe });
    case "excessive_cycling":
      return t("manage.ruleSum.excessiveCycling", { dp, count: rule.config.max_cycles, timeframe: rule.config.timeframe });
    case "peak_demand": {
      const c = rule.config;
      return t("manage.ruleSum.peakDemand", { dp, pct: c.warning_percent, limit: c.demand_limit, dur: fmtDur(c.duration_s) });
    }
    case "idle_consumption":
      return t("manage.ruleSum.idleConsumption", { dp, value: rule.config.power_threshold, dur: fmtDur(rule.config.duration_s) });
    case "expression": {
      const expr = rule.config.expression;
      return expr.length > 80 ? `${expr.slice(0, 77)}…` : expr;
    }
    case "composite":
      return t("manage.ruleSum.composite", { count: rule.config.child_rule_ids.length, op: rule.config.operator.toUpperCase() });
    case "comm_degradation": {
      const c = rule.config;
      return t("manage.ruleSum.commDegradation", { dp, pct: c.loss_pct_threshold, hz: c.expected_rate_hz, dur: fmtDur(c.window_s) });
    }
  }
}

/** Catalog freshness fallback when the edge didn't flag `stale` itself. */
const STALE_AFTER_MS = 5 * 60_000;

function fmtValue(v: number): string {
  if (!Number.isFinite(v)) return String(v);
  return Math.abs(v) >= 100 ? v.toFixed(0) : String(Math.round(v * 10) / 10);
}

/**
 * "now 12.4 %" — the datapoint's last observed value from the device's
 * `available_datapoints` catalog, with a stale marker once the reading is
 * old (edge `stale` flag, or last_seen beyond {@link STALE_AFTER_MS}).
 * Renders nothing when the topic was never observed — no fabricated zeros.
 */
function NowValue({ info }: { info: DatapointInfo | undefined }) {
  const t = useT();
  if (!info || info.last_value == null || info.last_seen_ts === 0) return null;
  const ageMs = Date.now() - info.last_seen_ts;
  const stale = info.stale || ageMs > STALE_AFTER_MS;
  const v = info.last_value;
  const text = typeof v === "number" ? fmtValue(v) : String(v);
  return (
    <span
      className={cn(
        "text-[11px] whitespace-nowrap shrink-0",
        stale ? "text-warn" : "text-text-muted",
      )}
    >
      {t("manage.rules.now")}{" "}
      <span className="font-mono">
        {text}
        {info.unit ? ` ${info.unit}` : ""}
      </span>
      {stale && <> · {t("manage.rules.seen", { age: formatRelativeAge(ageMs) })}</>}
    </span>
  );
}

export interface DeviceRuleSet {
  device: AlarmTargetableDevice;
  rules: AlarmRule[];
  /** ms epoch of the most recent `saveAlarmRules` against this device. */
  pendingSince?: number;
}

interface Props {
  perDevice: DeviceRuleSet[];
  /** Per-device `available_datapoints` catalog (keyed by device id) — drives the "now …" live value. */
  datapointsByDevice: Record<string, DatapointInfo[]>;
  /**
   * Resolve a /historical deep-link for a rule's datapoint, or null when
   * the topic doesn't map to a plottable series (the link is then omitted
   * rather than landing on the wrong chart).
   */
  historyHrefFor: (device: AlarmTargetableDevice, rule: AlarmRule) => string | null;
  onEditOne: (deviceId: string, ruleId: string) => void;
  onEditAll: (templateId: string) => void;
  onToggleEnabled: (deviceId: string, ruleId: string, enabled: boolean) => void;
  onDelete: (deviceId: string, ruleId: string) => void;
  onDeleteGroup: (templateId: string) => void;
  /**
   * F21: forwarded to each per-device AckStatusBadge so the page can mirror
   * the edge ACK (acknowledged / edge_error / timeout) into the shared
   * pending-writes store. Optional — the badge still renders without it.
   */
  onDeviceAck?: (deviceId: string, state: AckState) => void;
}

interface Group {
  key: string;
  templateId: string | null;
  name: string;
  rules: Array<{ device: AlarmTargetableDevice; rule: AlarmRule }>;
}

function groupRules(perDevice: DeviceRuleSet[]): Group[] {
  const byKey = new Map<string, Group>();
  for (const { device, rules } of perDevice) {
    for (const rule of rules) {
      const key = rule.template_id ?? `__no_tpl__:${rule.id}`;
      let group = byKey.get(key);
      if (!group) {
        group = {
          key,
          templateId: rule.template_id ?? null,
          name: rule.name,
          rules: [],
        };
        byKey.set(key, group);
      }
      group.rules.push({ device, rule });
    }
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function RuleList({
  perDevice,
  datapointsByDevice,
  historyHrefFor,
  onEditOne,
  onEditAll,
  onToggleEnabled,
  onDelete,
  onDeleteGroup,
  onDeviceAck,
}: Props) {
  const t = useT();
  const groups = useMemo(() => groupRules(perDevice), [perDevice]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const dpFor = (deviceId: string, rule: AlarmRule): DatapointInfo | undefined => {
    if (!rule.datapoint) return undefined;
    return datapointsByDevice[deviceId]?.find((d) => d.topic === rule.datapoint);
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (groups.length === 0) {
    return (
      <div className="border border-edge rounded p-6 text-center text-text-faint text-[12px]">
        {/* "Templates" is the (not-yet-localized) gallery button's literal
            name — referenced verbatim so the hint matches the button. */}
        {t("manage.rules.empty.pre")} <strong>+ {t("manage.rules.newRule")}</strong>{" "}
        {t("manage.rules.empty.or")} <strong>Templates</strong> {t("manage.rules.empty.post")}
      </div>
    );
  }

  return (
    <div className="border border-edge rounded divide-y divide-edge/60 bg-surface">
      {groups.map((g) => {
        const isOpen = expanded.has(g.key);
        const first = g.rules[0]?.rule;
        const replicaCount = g.rules.length;
        const anyDisabled = g.rules.some((x) => !x.rule.enabled);
        return (
          <div key={g.key}>
            <div
              role="button"
              tabIndex={0}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-2/40"
              onClick={() => toggleExpand(g.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleExpand(g.key);
                }
              }}
            >
              {isOpen ? (
                <ChevronDown className="size-3.5 text-text-faint" />
              ) : (
                <ChevronRight className="size-3.5 text-text-faint" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold truncate">
                  {g.name}
                  <span className="ml-2 text-[11px] text-text-faint">
                    · {replicaCount === 1
                      ? t("manage.rules.devices.one", { count: 1 })
                      : t("manage.rules.devices.many", { count: replicaCount })}
                  </span>
                  {anyDisabled && (
                    <span className="ml-2 text-[10px] uppercase text-warn">
                      {t("manage.rules.hasDisabled")}
                    </span>
                  )}
                </div>
                {/* What the rule actually checks — visible without opening
                    the editor (ALM-08). */}
                {first && (
                  <div
                    className="text-[11px] text-text-faint font-mono truncate"
                    title={summarizeRule(first, t)}
                  >
                    {summarizeRule(first, t)}
                  </div>
                )}
              </div>
              {/* Single-replica groups show the live value right in the
                  header; multi-device groups show it per row below (the
                  value differs per device). */}
              {replicaCount === 1 && first && (
                <NowValue info={dpFor(g.rules[0].device.id, first)} />
              )}
              {first && (
                <span
                  className={cn(
                    "text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold",
                    severityBadgeClass(first.severity),
                  )}
                >
                  {sevLabel(t, first.severity)}
                </span>
              )}
              <span
                className="text-[11px] text-text-faint font-mono cursor-help"
                title={first ? t(RULE_TYPE_LABEL_KEYS[first.type]) : undefined}
              >
                {first?.type}
              </span>
              {g.templateId && replicaCount > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditAll(g.templateId!);
                  }}
                  className="text-[11px] uppercase tracking-wide px-2 py-1 rounded border border-edge hover:bg-surface-2/60"
                >
                  {t("manage.rules.editAll")}
                </button>
              )}
              {g.templateId && replicaCount > 1 && (
                <HoldToConfirm
                  label={t("manage.rules.deleteAll")}
                  intent="destructive"
                  onConfirm={() => onDeleteGroup(g.templateId!)}
                />
              )}
            </div>

            {isOpen && (
              <div className="bg-surface-2/30 border-t border-edge/40 divide-y divide-edge/40">
                {g.rules.map(({ device, rule }) => {
                  const set = perDevice.find((p) => p.device.id === device.id);
                  const historyHref = historyHrefFor(device, rule);
                  return (
                    <div
                      key={device.id + ":" + rule.id}
                      className="px-3 py-2 flex items-center gap-3"
                    >
                      <span className="text-[11px] font-mono text-text-faint flex-1 min-w-0 truncate">
                        {device.name} · {rule.id}
                      </span>
                      <NowValue info={dpFor(device.id, rule)} />
                      {historyHref && (
                        <Link
                          to={historyHref}
                          className="text-[11px] text-accent hover:underline whitespace-nowrap shrink-0"
                          title={t("manage.rules.historyTitle")}
                        >
                          {t("manage.rules.history")}
                        </Link>
                      )}
                      <label
                        className="flex items-center gap-1 text-[11px]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={(e) =>
                            onToggleEnabled(device.id, rule.id, e.target.checked)
                          }
                        />
                        {t("manage.rules.enabled")}
                      </label>
                      <AckStatusBadge
                        deviceId={device.id}
                        pendingSince={set?.pendingSince}
                        onAck={onDeviceAck ? (s) => onDeviceAck(device.id, s) : undefined}
                      />
                      <button
                        type="button"
                        onClick={() => onEditOne(device.id, rule.id)}
                        className="text-text-faint hover:text-text"
                        aria-label={t("manage.rules.editRule")}
                      >
                        <Pencil className="size-3.5" />
                      </button>
                      <HoldToConfirm
                        label={t("manage.rules.delete")}
                        intent="destructive"
                        onConfirm={() => onDelete(device.id, rule.id)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
